import type { ImageArray } from "@pdfking/compatibility"
import type { Pixel, RGBColor } from "@pdfking/math"
import { Round } from "@pdfking/math"
import { IsArray, IsNumber, RandomInteger, Range, Zip } from "@pdfking/tfunc"

type ImageSize = [Pixel, Pixel] | Pixel
type ColorRange = [RGBColor, RGBColor] | RGBColor

const DEFAULT_SIZE = 150 as Pixel

const BLACK: RGBColor = { alpha: 1, blue: 0, green: 0, red: 0 } as RGBColor
const WHITE: RGBColor = { alpha: 1, blue: 1, green: 1, red: 1 } as RGBColor

const randomChannel = (n: number, min: number, max: number, seed?: number) =>
  RandomInteger(n, Round(min * 255), Round(max * 255), seed)

const normalizeSize = (size: ImageSize): [Pixel, Pixel] =>
  IsArray(size) ? size : [size, size]

/**
 * Generates a random image with pixel colors in specified RGB range.
 *
 * @param color - Color range specification:
 *   - RGBColor: range from black to provided color
 *   - [RGBColor, RGBColor]: range from first to second color
 * @param size - Image dimensions:
 *   - number: creates square image of {size, size}
 *   - [width, height]: rectangular dimensions
 * @param seed - Random seed for reproducibility (optional, random if not provided)
 * @returns ImageArray containing random pixel values
 */
export const RandomImage = (
  color: ColorRange = WHITE,
  size: ImageSize = DEFAULT_SIZE,
  seed?: number
): ImageArray => {
  if (!IsArray(color)) {
    return RandomImage([BLACK, color], size, seed)
  }

  const [
    { alpha: a1, blue: b1, green: g1, red: r1 },
    { alpha: a2, blue: b2, green: g2, red: r2 },
  ] = color
  const [width, height] = normalizeSize(size)
  const n = width * height

  const pixels = Zip(
    Range(0, (n - 1) * 4, 4),
    randomChannel(n, r1, r2, seed),
    randomChannel(n, g1, g2, IsNumber(seed) ? seed + 1 : undefined),
    randomChannel(n, b1, b2, IsNumber(seed) ? seed + 2 : undefined),
    randomChannel(n, a1, a2, IsNumber(seed) ? seed + 3 : undefined)
  )

  const data = new Uint8Array(n * 4)

  for (const [i, r, g, b, a] of pixels) {
    data[i] = r
    data[i + 1] = g
    data[i + 2] = b
    data[i + 3] = a
  }

  return { data, height, width }
}
